export const USER_ROLES = {
  SUPER_ADMIN: 'super_admin',
  ADMIN: 'admin',
  MODERATOR: 'moderator',
  IDEA_MANAGER: 'idea_manager',
  MEMBER: 'member',
} as const;

export type UserRole = (typeof USER_ROLES)[keyof typeof USER_ROLES];

export const USER_ROLE_VALUES = Object.values(USER_ROLES) as [UserRole, ...UserRole[]];

/** Roles allowed to moderate the Ideas and Praises modules. */
export const MODERATOR_ROLES = [USER_ROLES.ADMIN, USER_ROLES.MODERATOR] as const;

/**
 * Privilege ordering. Higher rank may manage everyone strictly below it.
 * IDEA_MANAGER shares the member rank on purpose: its extra powers are scoped to the Ideas module,
 * so every rank-based guard (moderator and up) keeps it out of the rest of the admin area by default.
 */
export const ROLE_RANK: Record<UserRole, number> = {
  [USER_ROLES.MEMBER]: 0,
  [USER_ROLES.IDEA_MANAGER]: 0,
  [USER_ROLES.MODERATOR]: 1,
  [USER_ROLES.ADMIN]: 2,
  [USER_ROLES.SUPER_ADMIN]: 3,
};

/** True when `role` ranks at or above `min`. */
export const hasAtLeastRole = (role: UserRole, min: UserRole): boolean => ROLE_RANK[role] >= ROLE_RANK[min];

/** Can moderate Ideas + Praises (status transitions, author/voter visibility). Moderator and up. */
export const canModerate = (role: UserRole): boolean => hasAtLeastRole(role, USER_ROLES.MODERATOR);

/** Can administer People + Access (users, admins, allowed domains, invites) and edit moderation config. Admin and up. */
export const canAdminister = (role: UserRole): boolean => hasAtLeastRole(role, USER_ROLES.ADMIN);

/** Can edit any idea, praise or direct feedback at any time — no author check, no edit window. Admin and up. */
export const canEditAnySubmission = (role: UserRole): boolean => canAdminister(role);

/** An actor may mutate a target only when ranked strictly above the target's current role. */
export const canManageUser = (actor: UserRole, targetCurrent: UserRole): boolean =>
  ROLE_RANK[actor] > ROLE_RANK[targetCurrent];

/** Can moderate Ideas only (status transitions, author visibility, export, award points). Manager and up, plus idea manager. */
export const canModerateIdeas = (role: UserRole): boolean => canModerate(role) || role === USER_ROLES.IDEA_MANAGER;

/** Can create, edit, reorder and delete idea categories. Admin and up, plus idea manager. */
export const canManageCategories = (role: UserRole): boolean => canAdminister(role) || role === USER_ROLES.IDEA_MANAGER;

/** Can enter the /admin area at all. Idea moderation is the broadest admin capability. */
export const canAccessAdmin = canModerateIdeas;

/** Only admins and up change roles, and only to roles ranked strictly below their own. */
export const canAssignRole = (actor: UserRole, newRole: UserRole): boolean =>
  canAdminister(actor) && ROLE_RANK[actor] > ROLE_RANK[newRole];

/** Roles this actor may assign through the UI. Never includes super_admin — that role is env-bootstrapped only. */
export const assignableRoles = (actor: UserRole): UserRole[] =>
  USER_ROLE_VALUES.filter((r) => r !== USER_ROLES.SUPER_ADMIN && canAssignRole(actor, r));
